import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  InputAdornment,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import styles from '../../content/pages/home.module.css';
import { PureLightTheme } from 'src/theme/schemes/PureLightTheme';
import React, { useContext, useState } from 'react';
import { updatePlayersRTCValues } from 'src/api/postRotoOffset';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { makeStyles } from '@mui/styles';
import { Edit, EditNotifications } from '@mui/icons-material';

const columns = [
  { id: 'rank', label: 'Rank' },
  { id: 'player', label: 'Player' },
  { id: 'pos', label: 'Pos' },
  { id: 'team', label: 'Team' },
  { id: 'algo', label: 'Algo' }
];

const useStyles = makeStyles((theme) => ({
  root: {
    '& .css-1oy3kq-MuiInputBase-root-MuiTablePagination-select': {
      marginRight: 0,
      marginLeft: 0
    }
  }
}));

function PlayersTable({ rtcValues, setRtcValues, positionFilter }) {
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [sliderValue, setSliderValue] = useState({});
  const [originalSliderValue, setOriginalSliderValue] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const { token } = useContext(SidebarContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  // State to track the key of player being edited
  const [editingKey, setEditingKey] = useState(null);
  const [inputValue, setInputValue] = useState({});
  // State and function for handling double touch on mobile
  const [lastTap, setLastTap] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to restore original slider value
  const restoreOriginalSliderValue = (key, originalSliderValue, player, setSliderValue) => {
    setSliderValue((prevSliderValue) => ({
      ...prevSliderValue,
      [key]: originalSliderValue[key] !== undefined ? originalSliderValue[key] : player.offset || 0
    }));
  };

  const handleExpandClick = (player) => {
    //Using this combination of dtcId and name because there are multiple players with the same dtcId
    const playerKey = `${player.dtcId}_${player.name}`;

    if (expandedPlayer === playerKey) {
      // Collapse player
      setExpandedPlayer(null);

      // Restore slider value if there are unsaved changes
      if (unsavedChanges && sliderValue[playerKey] !== undefined) {
        restoreOriginalSliderValue(playerKey, originalSliderValue, player, setSliderValue);
      } else {
        setSliderValue((prevSliderValue) => ({
          ...prevSliderValue,
          [playerKey]: player.offset || 0
        }));
      }
      setUnsavedChanges(false); // Reset unsaved changes flag
    } else {
      // Expand player
      if (expandedPlayer) {
        // Restore original slider value of previously expanded player
        restoreOriginalSliderValue(expandedPlayer, originalSliderValue, player, setSliderValue);
        setUnsavedChanges(false); // Reset unsaved changes for previous player
      }
      // Set new player as expanded
      setExpandedPlayer(playerKey);
      // Save current slider value if not already saved
      if (!originalSliderValue[playerKey]) {
        setOriginalSliderValue((prevOriginalSliderValue) => ({
          ...prevOriginalSliderValue,
          [playerKey]: player.offset || 0
        }));
      }

      // Set current slider value
      setSliderValue((prevSliderValue) => ({
        ...prevSliderValue,
        [playerKey]: sliderValue[playerKey] !== undefined ? sliderValue[playerKey] : player.offset || 0
      }));
    }
  };
  // Function to detect if the device is iOS
  function iOS() {
    const userAgent = navigator.userAgent || '';

    // Detect iOS through the userAgent
    return (
      /iPad|iPhone|iPod/.test(userAgent) ||
      // iPad detection on iOS 13 and later.
      (userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  const isIOS = iOS();

  const handleSliderChange = (event, newValue) => {
    // Avoid handling mousedown event on iOS devices
    if (isIOS && event.type === 'mousedown') {
      return;
    }
    const playerKey = expandedPlayer;
    if (sliderValue[playerKey] !== newValue) {
      setSliderValue((prevSliderValue) => ({
        ...prevSliderValue,
        [playerKey]: newValue
      }));
      setUnsavedChanges(true);
    }
  };

  //Update rotoOffset of current value for backend
  const handleSaveChanges = async () => {
    if (!unsavedChanges) {
      return; //Unsaved changes, nothing happens
    }
    // Updates array with dtcId and offset for backend
    const updates = Object.keys(sliderValue)
      .filter((playerKey) => {
        const [dtcId, name] = playerKey.split('_');
        const player = rtcValues.find((player) => player.dtcId === dtcId && player.name === name);
        return player && sliderValue[playerKey] !== player.offset;
      })
      .map((playerKey) => {
        const [dtcId, name] = playerKey.split('_');
        return {
          dtcId,
          name,
          offset: sliderValue[playerKey] || 0
        };
      });
    console.log('Sending updates to backend:', updates);

    try {
      const result = await updatePlayersRTCValues(token, updates);

      // Check if the update was successful based on the response
      if (result && result.message === 'Offsets updated successfully') {
        // Update rtcValues with the updated players
        const updatedPlayers = result.updated_players;

        // Update rtcValues with the updated players
        const newRtcValues = rtcValues.map((player) => {
          const playerUpdated = updatedPlayers.find((updatedPlayer) => updatedPlayer.dtcId === player.dtcId);

          if (playerUpdated) {
            return {
              ...player,
              offset: playerUpdated.offset,
              previous_offset: playerUpdated.previous_offset,
              update_datetime_dtc: player.update_datetime_dtc,
              update_datetime_roto: player.update_datetime_roto,
              update_datetime: playerUpdated.update_datetime, //When the last update was done
              updated_by: playerUpdated.updated_by //Who did the last update
            };
          }
          return player;
        });

        // Sort newRtcValues based on new value Algo
        newRtcValues.sort((a, b) => {
          let algoA = parseFloat(a.rtc_value || 0) + (sliderValue[`${a.dtcId}_${a.name}`] || a.offset || 0);
          let algoB = parseFloat(b.rtc_value || 0) + (sliderValue[`${b.dtcId}_${b.name}`] || b.offset || 0);

          // If sliderValue is 0, use only rtc_value for comparing (this is to contemplate if the offset is 0, to successfully reposition the player on the table in this case)
          if (sliderValue[`${a.dtcId}_${a.name}`] === 0) {
            algoA = parseFloat(a.rtc_value || 0);
          }
          if (sliderValue[`${b.dtcId}_${b.name}`] === 0) {
            algoB = parseFloat(b.rtc_value || 0);
          }

          return algoB - algoA;
        });

        setRtcValues(newRtcValues);
        setExpandedPlayer(null);
      } else {
        console.error('Failed to update RTC values:', result && result.message);
      }
    } catch (error) {
      console.error('Error updating RTC values:', error);
    } finally {
      // Reset unsavedChanges state
      setUnsavedChanges(false);
    }
  };

  const playerValues = ['Algo', 'Offset', 'Total'];

  // Filter players based on positionFilter
  // const filteredRtcValues = rtcValues.filter((player) => positionFilter === null || player.position === positionFilter);
  const filteredRtcValues = rtcValues.filter((player) => {

    const validPositions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    if (!validPositions.includes(player.position)) {
      console.log(`Avoid player: ${player.name} - Position: ${player.position}`)
      return false; 
    }
    if (!positionFilter) {
      return true; // Render all players
    } else if (Array.isArray(positionFilter)) {
      return !positionFilter.includes(player.position);
    } else {
      return player.position === positionFilter;
    }
  });

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const playersToShow = filteredRtcValues.slice(startIndex, endIndex);

  //This function formats the values of Algo, Offset and Total to '0' in case they become null, undefined or NaN
  const formatValue = (value) => {
    if (isNaN(value) || value === null || value === undefined) {
      return '0';
    }
    return parseFloat(value).toFixed(1);
  };

  // Function to get the last edit text for a player using dates, times and who did the last edit
  const getLastEditText = (player) => {
    if (player.update_datetime) {
      const date = new Date(player.update_datetime * 1000); // Convert to milliseconds
      const formattedDate = date.toLocaleDateString();
      const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

      if (player.updated_by === 'ROTO') {
        return `Last edit by ROTO on ${formattedDate} at ${formattedTime}`;
      } else if (player.updated_by === 'DTC') {
        return `Last edit by DTC on ${formattedDate} at ${formattedTime}`;
      } else {
        return `Last edit on ${formattedDate} at ${formattedTime}`;
      }
    } else {
      // If update_datetime doesn't exist, use update_datetime_dtc or update_datetime_roto if available
      if (player.update_datetime_dtc && player.update_datetime_roto) {
        // Compare dates of DTC o Roto and use the latest one
        const dtcDate = new Date(player.update_datetime_dtc * 1000);
        const rotoDate = new Date(player.update_datetime_roto * 1000);

        if (dtcDate > rotoDate) {
          const formattedDtcDate = dtcDate.toLocaleDateString();
          const formattedDtcTime = dtcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          return `Last edit by DTC on ${formattedDtcDate} at ${formattedDtcTime}`;
        } else {
          const formattedRotoDate = rotoDate.toLocaleDateString();
          const formattedRotoTime = rotoDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          return `Last edit by ROTO on ${formattedRotoDate} at ${formattedRotoTime}`;
        }
      } else if (player.update_datetime_dtc) {
        const dtcDate = new Date(player.update_datetime_dtc * 1000);
        const formattedDtcDate = dtcDate.toLocaleDateString();
        const formattedDtcTime = dtcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        return `Last edit by DTC on ${formattedDtcDate} at ${formattedDtcTime}`;
      } else if (player.update_datetime_roto) {
        const rotoDate = new Date(player.update_datetime_roto * 1000);
        const formattedRotoDate = rotoDate.toLocaleDateString();
        const formattedRotoTime = rotoDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        return `Last edit by ROTO on ${formattedRotoDate} at ${formattedRotoTime}`;
      } else {
        return 'No edit history available';
      }
    }
  };

  // Function to handle changes in input and update the inputValue state
  const handleInputChange = (event, playerKey) => {
    const newValue = event.target.value;
    setInputValue((prev) => ({ ...prev, [playerKey]: newValue }));
  };

  // Function to set the value of the input and update the sliderValue if there is an actual change in it
  const applyInputValue = (playerKey) => {
    const newValue = parseFloat(inputValue[playerKey] || 0);
    if (sliderValue[playerKey] !== newValue) {
      setSliderValue((prev) => ({ ...prev, [playerKey]: newValue }));
      setUnsavedChanges(true);
    }
    setEditingKey(null);
  };

  const handleDoubleTap = (playerKey) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // Time in ms to count as a double touch

    if (lastTap && now - lastTap < DOUBLE_TAP_DELAY) {
      setEditingKey(playerKey);
      setInputValue((prev) => ({ ...prev, [playerKey]: sliderValue[playerKey].toString() }));
    } else {
      setLastTap(now); // Save the time of the last touch
    }
  };

  const classes = useStyles();

  return (
    <>
      <TableContainer sx={{ overflow: 'hidden', overflowY: 'auto', pt: 2, pl: 3, pr: 3 }} className={classes.root}>
        <Table sx={{ bgcolor: `${PureLightTheme.colors.alpha.black[100]}` }}>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              sx={{
                borderColor: 'none',
                padding: 1,
                fontSize: '12px',
                color: `${PureLightTheme.colors.alpha.white[100]}`,
                borderBottom: '0px ',
                textAlign: column.id === 'player' ? 'left' : 'center'
              }}
            >
              {column.label}
            </TableCell>
          ))}

          <TableBody>
            {playersToShow.map((player, index) => {
              const playerKey = `${player.dtcId}_${player.name}`;
              return (
                <React.Fragment key={playerKey}>
                  <TableRow
                    key={playerKey}
                    onClick={() => handleExpandClick(player)}
                    style={{
                      borderRight: '2px solid #000000',
                      height: '45px',
                      border: '1px solid white',
                      backgroundColor: '#555555',
                      marginBottom: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    <TableCell
                      sx={{
                        p: 0,
                        fontSize: '14px',
                        textAlign: 'center',
                        color: `${PureLightTheme.colors.alpha.white[100]}`,
                        borderBottom: '1px solid white',
                        bgcolor: '#777777'
                      }}
                    >
                      {/* {positionFilter === null ? player.rank : player.sub_rank} */}
                      {index + 1}
                    </TableCell>
                    <TableCell
                      sx={{ p: 0.2, pl: 1, fontSize: '14px', color: `${PureLightTheme.colors.alpha.white[100]}`, borderBottom: '1px solid white' }}
                    >
                      {player.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        fontSize: '14px',
                        textAlign: 'center',
                        color: `${PureLightTheme.colors.alpha.white[100]}`,
                        borderBottom: '1px solid white'
                      }}
                    >
                      {player.position}
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        fontSize: '14px',
                        textAlign: 'center',
                        color: `${PureLightTheme.colors.alpha.white[100]}`,
                        borderBottom: '1px solid white',
                        width: '54px'
                      }}
                    >
                      {player.team}
                    </TableCell>
                    <TableCell
                      sx={{
                        p: 0,
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: `${PureLightTheme.colors.alpha.white[100]}`,
                        borderBottom: '1px solid white',

                        bgcolor: (() => {
                          const colorOffset = sliderValue[playerKey] !== undefined ? sliderValue[playerKey] : player.offset;
                          return colorOffset === 0 ? '#777777' : colorOffset > 0 ? '#399000' : colorOffset < 0 ? '#B00000' : '#777777';
                        })()
                      }}
                    >
                      {/* Function for calculate Algo column (same value as client total), rotoTotal (algorithm + roto offset) + client offset */}
                      {(() => {
                        const rtcValue = player.rtc_value || 0;
                        const sliderOffset = sliderValue[playerKey] !== undefined ? sliderValue[playerKey] : player.offset || 0;
                        const totalValue = parseFloat(rtcValue) + sliderOffset;
                        return totalValue.toFixed(1);
                      })()}
                    </TableCell>
                  </TableRow>

                  {/* Expand the accordion player by index */}
                  {expandedPlayer === playerKey && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          textAlign: 'center',
                          padding: 0.5,
                          borderBottom: 'none',
                          bgcolor: '#333333',
                          color: `${PureLightTheme.colors.alpha.white[100]}`
                        }}
                      >
                        <Typography variant="body1" sx={{ fontSize: '10px', color: '#999999', textAlign: 'center' }}>
                          ROTO OFFSET
                        </Typography>
                        {/* <Slider size="small" defaultValue={70} aria-label="Small" valueLabelDisplay="auto" /> */}
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" pr={1} pl={1}>
                          <Slider
                            defaultValue={0}
                            value={sliderValue[playerKey]}
                            onChange={handleSliderChange}
                            min={-20}
                            // min={parseFloat((-rtcValues[expandedPlayer]?.rtc_value).toFixed(1))}
                            max={20}
                            // max={parseFloat((rtcValues[expandedPlayer]?.rtc_value).toFixed(1))}
                            aria-label="Default"
                            valueLabelDisplay="auto"
                            color="secondary"
                            //These two next properties are for choose decimal numbers in slider
                            step={0.1}
                            valueLabelFormat={(value) => value.toFixed(1)}
                          />
                        </Box>

                        <Typography variant="body1" sx={{ fontSize: '10px', color: '#999999', textAlign: 'center', p: 0.5 }}>
                          {/* Execute function to render dates of edits  */}
                          {getLastEditText(player)}
                        </Typography>

                        <Box textAlign="center">
                          <Button
                            onClick={handleSaveChanges}
                            variant="contained"
                            size="small"
                            sx={{
                              p: 0.5,
                              lineHeight: 1,
                              backgroundColor: sliderValue[playerKey] !== player.offset ? 'info.main' : '#555555',
                              '&:hover': {
                                backgroundColor: sliderValue[playerKey] !== player.offset ? 'info.main' : '#555555'
                              },
                              '&:active': {
                                backgroundColor: sliderValue[playerKey] !== player.offset ? 'info.main' : '#555555'
                              }
                            }}
                          >
                            Save
                          </Button>
                        </Box>
                        <Typography variant="body1" sx={{ fontSize: '10px', color: '#999999', textAlign: 'center', p: 0.5 }}>
                          CURRENT
                        </Typography>

                        <ButtonGroup
                          variant="contained"
                          color="inherit"
                          aria-label="Basic button group"
                          sx={{
                            '& .MuiButton-root:hover': {
                              backgroundColor: '#555555 !important',
                              cursor: 'default'
                            }
                          }}
                        >
                          {playerValues.map((column, index) => (
                            <Button key={index} sx={{ backgroundColor: '#555555', p: 0, pt: 0.5, pb: 0.5, minWidth: '80px', width: '100px' }}>
                              <Grid container direction="column" alignItems="center" justifyContent="center" spacing={0}>
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#ffffff', textAlign: 'center' }}>
                                  {column === 'Algo' ? formatValue(rtcValues.find((p) => `${p.dtcId}_${p.name}` === playerKey)?.rtc_value) : null}

                                  {/* {column === 'Offset' ? formatValue(sliderValue[playerKey]) : null} */}

                                  {column === 'Offset' ? (
                                    editingKey === playerKey ? (
                                      <TextField
                                        value={inputValue[playerKey] ?? sliderValue[playerKey]}
                                        onChange={(e) => handleInputChange(e, playerKey)}
                                        onBlur={() => applyInputValue(playerKey)}
                                        type="number"
                                        inputProps={{
                                          style: { color: 'white', padding: '0px', textAlign: 'center' }
                                        }}
                                        sx={{
                                          width: '70px',
                                          height: 'auto',
                                          textAlign: 'center',
                                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none', // Remove increment and decrement buttons
                                            margin: 0
                                          }
                                        }}
                                      />
                                    ) : (
                                      <>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            fontSize: '14px',
                                            color: '#ffffff !important',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            ml: 1.5,
                                            display: 'inline-flex', // Put typography and edit icon at the same level
                                            alignItems: 'center'
                                          }}
                                          onDoubleClick={() => {
                                            setEditingKey(playerKey);
                                            setInputValue((prev) => ({ ...prev, [playerKey]: sliderValue[playerKey].toString() }));
                                          }}
                                          onTouchEnd={() => handleDoubleTap(playerKey)}
                                        >
                                          {formatValue(sliderValue[playerKey])}
                                        </Typography>
                                        <Edit sx={{ fontSize: '0.7rem', ml: 0.5, mb: 0.5 }} />
                                      </>
                                    )
                                  ) : null}

                                  {column === 'Total'
                                    ? (() => {
                                        const player = rtcValues.find((p) => `${p.dtcId}_${p.name}` === playerKey);
                                        if (player) {
                                          const total = parseFloat(player.rtc_value) + parseFloat(sliderValue[playerKey] || 0);
                                          return formatValue(total);
                                        }
                                        return '0';
                                      })()
                                    : null}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '10px', color: '#ffffff', textAlign: 'center' }}>
                                  {column}
                                </Typography>
                              </Grid>
                            </Button>
                          ))}
                        </ButtonGroup>

                        <Typography variant="body1" sx={{ fontSize: '10px', color: '#999999', textAlign: 'center', p: 0.5 }}>
                          PREVIOUS
                        </Typography>
                        <ButtonGroup
                          variant="contained"
                          color="inherit"
                          aria-label="Basic button group"
                          sx={{
                            '& .MuiButton-root:hover': {
                              backgroundColor: '#555555 !important',
                              cursor: 'default'
                            }
                          }}
                        >
                          {playerValues.map((column, index) => (
                            <Button key={index} sx={{ backgroundColor: '#555555', p: 0, pt: 0.5, pb: 0.5, minWidth: '80px', width: '100px' }}>
                              <Grid container direction="column" alignItems="center" justifyContent="center" spacing={0}>
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#ffffff', textAlign: 'center' }}>
                                  {column === 'Algo'
                                    ? formatValue(rtcValues.find((p) => `${p.dtcId}_${p.name}` === playerKey)?.previous_rtc_value)
                                    : null}

                                  {column === 'Offset'
                                    ? formatValue(rtcValues.find((p) => `${p.dtcId}_${p.name}` === playerKey)?.previous_offset)
                                    : null}

                                  {column === 'Total'
                                    ? (() => {
                                        const player = rtcValues.find((p) => `${p.dtcId}_${p.name}` === playerKey);
                                        if (player) {
                                          const total = parseFloat(player.previous_rtc_value) + parseFloat(player.previous_offset);
                                          return formatValue(total);
                                        }
                                        return '0';
                                      })()
                                    : null}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '10px', color: '#ffffff', textAlign: 'center' }}>
                                  {column}
                                </Typography>
                              </Grid>
                            </Button>
                          ))}
                        </ButtonGroup>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#777777',
            border: '1px solid white',
            padding: 0,
            marginBottom: 2
          }}
        >
          <TablePagination
            rowsPerPageOptions={[100, 200, 300]}
            component="div"
            count={filteredRtcValues.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: 'white'
            }}
          />
        </Box>
      </TableContainer>
    </>
  );
}

export default PlayersTable;
